'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { LocationHydration } from 'stores/LocationStore';
import { Authentication } from 'components/Session';
import { useScroll } from 'hooks/useScroll';
import IconSymplaMinimal from 'assets/icons/logo-sympla-minimal.svg';
import { isMobile as getIsMobile } from 'utils/misc';
import IconSympla from 'assets/icons/logo-sympla.svg';
import NewHeaderContext from '../NewHeaderContext/NewHeaderContext';
import { MobileCTACreateEventBanner } from '../MobileCTACreateEventBanner/MobileCTACreateEventBanner';
import LocationButton from '../LocationButton/LocationButton';
import { useIsPartnerPage } from 'hooks/useIsPartnerPage';
import * as styles from './Navbar.css';
import classNames from 'classnames';
import { analytics, analyticsEvents } from 'lib/analytics';
import { useIsClubeIfoodDiscountPage } from 'hooks/useIsClubeIfoodDiscountPage';

interface NavbarProps {
  hasSession: boolean;
  locationHydration: LocationHydration;
  alwaysCollapsed?: boolean;
  isMobile?: boolean;
  isDark?: boolean;
  hasMossColor?: boolean;
}

const minHeight = {
  navbarCollapsedDesktop: 76,
  baseDesktop: 232,
  baseMobile: 212
};

function Navbar({
  hasSession,
  locationHydration,
  alwaysCollapsed = false,
  isMobile = false,
  isDark = false,
  hasMossColor = false
}: NavbarProps) {
  const isNavbarCollapsed = useScroll(alwaysCollapsed);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const { isPartnerPage } = useIsPartnerPage();
  const isClubeIfoodDiscountPage = useIsClubeIfoodDiscountPage();

  const [showProducerBanner, setShowProducerBanner] = useState(true);
  const [userClosedBanner, setUserClosedBanner] = useState(false);

  const paddingInitial = useMemo(() => {
    if (isMobile) return minHeight.baseMobile;
    return isNavbarCollapsed
      ? minHeight.navbarCollapsedDesktop
      : minHeight.baseDesktop;
  }, [isNavbarCollapsed, isMobile]);
  const [navPadding, setNavPadding] = useState(paddingInitial);

  useEffect(() => {
    if (!headerRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setNavPadding(entry.borderBoxSize[0].blockSize);
      }
    });
    resizeObserver.observe(headerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const handleBannerClose = () => {
    setUserClosedBanner(true);
  };

  const updateHeight = useCallback(() => {
    const heightElement = headerRef.current?.offsetHeight || paddingInitial;
    setNavPadding(heightElement);
  }, [paddingInitial]);

  useEffect(() => {
    if (!isMobile) return;

    const handleScroll = () => {
      const currentScroll = window.scrollY;

      if (currentScroll > 10) {
        setShowProducerBanner(false);
      } else {
        setShowProducerBanner(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  useEffect(() => {
    const isMobileWindow = getIsMobile();
    if (isMobileWindow !== isMobile && isNavbarCollapsed) updateHeight();
  }, []);

  const wrapperClass = showProducerBanner
    ? styles.bannerWrapper
    : classNames(styles.bannerWrapper, styles.bannerWrapperHidden);

  const innerClass = showProducerBanner
    ? styles.bannerInner
    : classNames(styles.bannerInner, styles.bannerInnerHidden);

  const logoClassName = isDark ? styles.LogoStyleDark : styles.LogoStyle;
  const containerClass = classNames(styles.container, {
    [styles.containerCollapsed]: isNavbarCollapsed,
    [styles.containerDark]: isDark
  });

  const containerClassMobile = classNames(
    styles.container,
    styles.filtersContainer,
    styles.containerMobile,
    {
      [styles.containerCollapsed]: isNavbarCollapsed,
      [styles.containerDark]: isDark
    }
  );

  const handleClickNavbarOptionAnalyticsTrack = (optionName = '') => {
    return () => {
      analytics.sendEvent(analyticsEvents.header_clicked, {
        link_header: optionName
      });
    };
  };

  return (
    <header className={`${isDark ? styles.containerDark : ''}`}>
      <nav
        ref={headerRef}
        className={`${styles.navbar} ${isDark ? styles.containerDark : ''}`}
      >
        {!userClosedBanner && (
          <div className={wrapperClass}>
            <div className={innerClass}>
              <MobileCTACreateEventBanner
                isMobile={isMobile}
                handleCloseBanner={handleBannerClose}
                isDark={isDark}
              />
            </div>
          </div>
        )}

        <div className={containerClass}>
          <Link
            href="/"
            aria-label="Sympla sua plataforma de eventos. Logo."
            title="Sympla sua plataforma de eventos"
            className={styles.LinkLogo}
            data-testid="header-navbar-logo"
            onClick={handleClickNavbarOptionAnalyticsTrack('logo')}
          >
            <div className={styles.containerMobile}>
              <IconSymplaMinimal
                width={32}
                height={32}
                className={logoClassName}
                alt="Sympla sua plataforma de eventos"
              />
            </div>
            <div className={styles.containerDesktop}>
              <div hidden={!isNavbarCollapsed}>
                <IconSymplaMinimal
                  width={28}
                  height={28}
                  className={logoClassName}
                  alt="Sympla sua plataforma de eventos"
                  hidden={!isNavbarCollapsed}
                />
              </div>
              <div hidden={isNavbarCollapsed}>
                <IconSympla
                  width={112}
                  height={48}
                  className={`${styles.containerDesktop} ${logoClassName}`}
                  alt="Sympla sua plataforma de eventos"
                  hidden={isNavbarCollapsed}
                />
              </div>
            </div>
          </Link>

          <div className={styles.containerMobile}>
            <LocationButton
              selectedLocation={locationHydration.selectedLocation}
              variant={isDark ? 'dark' : 'default'}
              useSlugParam={isClubeIfoodDiscountPage}
            />
          </div>

          <div
            className={`${styles.filtersContainer} ${
              isNavbarCollapsed ? styles.filtersContainerCollapsed : ''
            } ${styles.containerDesktop}`}
            data-testid="header-navbar-search-desktop"
          >
            <NewHeaderContext
              locationHydration={locationHydration}
              isNavbarCollapsed={isNavbarCollapsed}
              variant={isDark ? 'dark' : 'primary'}
              isDark={isDark}
              hasMossColor={hasMossColor}
            />
          </div>

          <div style={{ zIndex: 1 }} data-testid="header-navbar-auth">
            <Authentication
              isAuthenticated={hasSession}
              isNavbarCollapsed={isNavbarCollapsed}
              isDark={isDark}
              hasMossColor={hasMossColor}
            />
          </div>
        </div>

        {!isPartnerPage && (
          <div
            className={containerClassMobile}
            data-testid="header-navbar-search-mobile"
          >
            <NewHeaderContext
              locationHydration={locationHydration}
              isNavbarCollapsed={isNavbarCollapsed}
              isDark={isDark}
              hasMossColor={hasMossColor}
            />
          </div>
        )}
      </nav>

      <div
        style={{
          paddingTop: `${navPadding}px`,
          transition: 'padding-top 0.1s linear'
        }}
      />
    </header>
  );
}

export default Navbar;
